import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ApiConfiguration } from '../../api/api-configuration';

export interface CustomerDocsAccess {
  docHubUrl: string;
}

/** Sent by the doc hub when it hands a user back to SLM; the API validates both. */
export interface CustomerDocsAccessRequest {
  site?: string | null;
  rt?: string | null;
}

const DOC_HUB_DOMAIN = '.softco.com';
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

/** Angular's default codec leaves '+' unencoded, which the API would read as a space. */
const STRICT_QUERY_CODEC: HttpParameterCodec = {
  encodeKey: (key) => encodeURIComponent(key),
  encodeValue: (value) => encodeURIComponent(value),
  decodeKey: (key) => decodeURIComponent(key),
  decodeValue: (value) => decodeURIComponent(value),
};

/** Customer docs access (#52172); calls the API directly until the endpoint is in the generated client. */
@Injectable({ providedIn: 'root' })
export class CustomerDocsService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ApiConfiguration);
  private readonly document = inject(DOCUMENT);

  getAccess(request: CustomerDocsAccessRequest = {}): Observable<CustomerDocsAccess> {
    let params = new HttpParams({ encoder: STRICT_QUERY_CODEC });
    if (request.site) params = params.set('site', request.site);
    if (request.rt) params = params.set('rt', request.rt);
    return this.http
      .get<Partial<CustomerDocsAccess> | null>(`${this.config.rootUrl}/v1/customer-docs/access`, { params })
      .pipe(map((res) => ({ docHubUrl: this.toSafeDocHubUrl(res?.docHubUrl) })));
  }

  private toSafeDocHubUrl(raw: unknown): string {
    if (typeof raw !== 'string') throw new Error('Customer docs access returned no doc hub URL');
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      throw new Error('Customer docs access returned an invalid doc hub URL');
    }
    if (!this.isAllowedDocHub(url)) throw new Error('Customer docs access returned a doc hub URL that is not allowed');
    return url.toString();
  }

  private isAllowedDocHub(url: URL): boolean {
    const app = this.document.location;
    // The frame runs scripts under its own origin, so it must never be an SLM app origin.
    if (url.origin === app.origin) return false;
    if (LOCAL_HOSTS.has(app.hostname) && LOCAL_HOSTS.has(url.hostname)) {
      return url.protocol === 'http:' || url.protocol === 'https:';
    }
    if (url.protocol !== 'https:' || !url.hostname.endsWith(DOC_HUB_DOMAIN)) return false;
    const appParent = app.hostname.slice(app.hostname.indexOf('.') + 1);
    return url.hostname !== app.hostname && url.hostname !== appParent && !url.hostname.endsWith(`.${appParent}`);
  }
}
